/**
 * runtime.js —— 装配 vue-i18n + setLocale/getLocale。
 * 只有 en 打进入口包（它也是缺 key 时的回落语言）；其他语言首次用到时按需加载，各自一个 chunk。
 * locale 初值由调用方注入，挂载前 `await runtime.ready`，避免先闪一帧英文。
 */
import { createI18n as createVueI18n } from 'vue-i18n'
import en from './messages/en/index.js'
import { SUPPORTED_LOCALES, DEFAULT_LOCALE, LOCALE_META, isSupportedLocale } from './locales.js'

/**
 * @typedef {import('./locales.js').LocaleCode} LocaleCode
 * @typedef {import('./locales.js').LocaleMeta} LocaleMeta
 */

/** @type {Record<Exclude<LocaleCode, 'en'>, () => Promise<{ default: Record<string, unknown> }>>} */
const LOADERS = {
  zh: () => import('./messages/zh/index.js'),
  ja: () => import('./messages/ja/index.js'),
  ko: () => import('./messages/ko/index.js'),
  es: () => import('./messages/es/index.js'),
  pt: () => import('./messages/pt/index.js'),
}

/**
 * @typedef {object} CreateI18nOptions
 * @property {LocaleCode} [locale] 初值（注入）；缺省 `'en'`。非法值回落 `'en'`。
 */

/**
 * @typedef {object} I18nRuntime
 * @property {import('vue-i18n').I18n} i18n vue-i18n 实例，`app.use(i18n)`。
 * @property {Promise<void>} ready 初值语言加载完并生效。
 * @property {(code: LocaleCode) => Promise<void>} setLocale 先加载该语言再切换，加载期间保持原语言（非法值回落 en）。
 * @property {(code: LocaleCode) => Promise<void>} loadLocale 只加载不切换，供切换器预取。
 * @property {() => LocaleCode} getLocale 读当前 locale（供 api-sdk 取 `x-locale`）。
 * @property {LocaleMeta[]} locales 语言元数据数组（供渲染切换器）。
 */

/**
 * 唯一入口工厂：装配 vue-i18n 运行时。
 * @param {CreateI18nOptions} [opts]
 * @returns {I18nRuntime}
 */
export function createI18n(opts = {}) {
  const i18n = createVueI18n(
    /** @type {any} */ ({
      legacy: false, // Composition API 模式
      locale: DEFAULT_LOCALE,
      fallbackLocale: DEFAULT_LOCALE, // 缺失 key 自动回落 en，不显示原始 key、不抛错
      fallbackWarn: false,
      missingWarn: false,
      messages: { en },
      availableLocales: SUPPORTED_LOCALES,
    }),
  )

  // legacy:false 下 `i18n.global.locale` 是一个 ref；vue-i18n 重载类型在
  // checkJs 下无法精确推断该分支，故对 global 取一次窄化引用集中处理。
  const global = /** @type {{ locale: { value: LocaleCode }, mergeLocaleMessage: (code: string, msgs: Record<string, unknown>) => void }} */ (
    /** @type {unknown} */ (i18n.global)
  )

  /** @type {Map<LocaleCode, Promise<void>>} */
  const loading = new Map([[DEFAULT_LOCALE, Promise.resolve()]])
  /** 最后一次请求的语言；连点两种语言时只让后点的生效 */
  let requested = DEFAULT_LOCALE

  /** @param {LocaleCode} code */
  function loadLocale(code) {
    if (!loading.has(code)) {
      // merge 而不是 set：admin、studio 会往同一语言里追加自己的文案，不能被覆盖
      const pending = LOADERS[/** @type {Exclude<LocaleCode, 'en'>} */ (code)]()
        .then(module => global.mergeLocaleMessage(code, module.default))
      pending.catch(() => loading.delete(code))
      loading.set(code, pending)
    }
    return /** @type {Promise<void>} */ (loading.get(code))
  }

  /** @param {LocaleCode} code */
  async function setLocale(code) {
    const next = isSupportedLocale(code) ? code : DEFAULT_LOCALE
    requested = next
    await loadLocale(next)
    if (requested === next) global.locale.value = next
  }

  /** @returns {LocaleCode} */
  function getLocale() {
    return global.locale.value
  }

  const ready = setLocale(isSupportedLocale(opts.locale) ? opts.locale : DEFAULT_LOCALE)

  return { i18n, ready, setLocale, loadLocale, getLocale, locales: LOCALE_META }
}
